import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, Loading, PageHeader, Table, Td } from '../components/ui/primitives';
import { dateTime } from '../lib/format';
import type { CampaignRecord } from '../lib/types';

const STATUS_TONE: Record<string, 'good' | 'warn' | 'bad' | 'info' | 'neutral'> = {
  draft: 'neutral',
  scheduled: 'info',
  sending: 'info',
  paused: 'warn',
  completed: 'good',
  cancelled: 'neutral',
  circuit_broken: 'bad',
};

export function CampaignsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<{ campaigns: CampaignRecord[] }>('/api/campaigns'),
  });

  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Campaigns"
        description="A campaign is planned before it sends: the audience is resolved against suppression and verification, then paced across mailboxes inside the send window."
        actions={
          <Button variant="primary" onClick={() => navigate('/campaigns/new')}>
            New campaign
          </Button>
        }
      />

      <Card>
        {!data?.campaigns.length ? (
          <EmptyState
            title="No campaigns yet"
            body="Create one to see the send plan before anything leaves."
            action={
              <Button variant="primary" onClick={() => navigate('/campaigns/new')}>
                New campaign
              </Button>
            }
          />
        ) : (
          <Table head={['Campaign', 'Subject', 'Status', 'Scheduled', 'Started', 'Completed']}>
            {data.campaigns.map((c) => (
              <tr key={c.id}>
                <Td>
                  <Link to={'/campaigns/' + c.id} className="font-medium text-ink-900 underline">
                    {c.name}
                  </Link>
                </Td>
                <Td className="max-w-sm truncate text-ink-600">{c.subject}</Td>
                <Td>
                  <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{c.status.replace(/_/g, ' ')}</Badge>
                  {c.pausedReason && (
                    <div className="mt-1 max-w-xs text-[11px] leading-snug text-amber-700">{c.pausedReason}</div>
                  )}
                </Td>
                <Td className="text-xs">{dateTime(c.scheduleAt)}</Td>
                <Td className="text-xs">{dateTime(c.startedAt)}</Td>
                <Td className="text-xs">{dateTime(c.completedAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
